import { withTranslation } from '@application/i18n/with_translation';
import { DateDisplayFormats, DateService } from '@application/time/date.service';
import { Avatar, Box, Flex, Icon, Spacer, Text } from '@cellula/react';
import { primitiveColorTokens, tokens } from '@cellula/react-theme-patient';
import { LinkInMessage } from '@component/link_in_message.component';
import { ReadReceiptClientComponent } from '@feature/chat/_read_receipt_client.component';
import { FormCard } from '@feature/dynamic_form/form_card.component';
import { AttachedFile } from '@feature/file_manager/attached_file.component';
import { useService } from '@visiba/instantiation';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { forwardRef, HTMLAttributes, Ref, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ObfuscateNameContext } from './_obfuscate_name.context';
import { ReadUntilContainer } from './_read_until_container.component';
import type { ControlledChatMessage } from './common';

interface Props extends HTMLAttributes<HTMLDivElement> {
	isMessageStreak: boolean;
	isNewSender: boolean;
	isLastMessageInStreak: boolean;
	message: ControlledChatMessage;
	onRead(message: ControlledChatMessage): void;
	windowHasFocus: boolean;
	peerReadTimestamp?: string | null;
	peerReadFullName?: string;
}

const elements = {
	container: styled.div`
		container: ChatBubbleContainer / inline-size;
		display: flex;
		padding: 0 ${tokens.spacing.x2};
		position: relative;

		@media screen and (min-width: 905px) {
			padding: 0 ${tokens.spacing.x4};
		}
	`,
	imageContainer: styled.div`
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		flex-shrink: 0;
		margin-right: ${tokens.spacing.x1};
		width: 32px;
	`,
	inner: styled.div<{ me: boolean }>`
		display: flex;
		flex-wrap: wrap;
		flex-direction: column;
		width: 100%;
		align-items: ${({ me }) => (me ? 'flex-end' : 'flex-start')};
	`,
	senderInfoContainer: styled.div`
		font: ${tokens.text.label.xSmallRegular};
		color: ${tokens.color.content.muted};
		margin: ${tokens.spacing.x2_5} 0 ${tokens.spacing.x0_5};
	`,
	messageBubble: styled(motion.div)`
		display: flex;
		flex-direction: column;
		padding: ${tokens.spacing.x1} ${tokens.spacing.x1_5};
		max-width: 250px;
		box-shadow: ${tokens.elevation.skim};
		border-radius: ${tokens.borderRadius.large};
		margin-top: ${tokens.spacing.x0_5};
		word-break: break-word;
		white-space: pre-line;
		user-select: text;
		color: ${tokens.color.content.default};

		@media screen and (min-width: 905px) {
			max-width: 380px;
		}

		&.other {
			background-color: ${primitiveColorTokens.neutral[100]};
			transform-origin: 0 100%;
			border-bottom-left-radius: 0;

			&.streak {
				border-top-left-radius: 0;
			}
		}

		&.me {
			background-color: ${tokens.color.bg.surfaceBrand};
			color: ${tokens.color.content.brand};
			transform-origin: 100% 100%;
			border-bottom-right-radius: 0;
			align-items: flex-end;

			&.streak {
				border-top-right-radius: 0;
			}
		}
	`,
	messageAnchorTag: styled.a`
		color: ${tokens.color.content.interactive};
		transition: text-decoration 100ms;

		&:hover {
			color: ${tokens.color.content.interactive};
			text-decoration: underline;
		}
	`,
	timestamp: styled.time`
		display: flex;
		font: ${tokens.text.label.xSmallRegular};
		color: ${tokens.color.content.muted};
		text-align: right;
		margin-top: ${tokens.spacing.x1};
	`,
	attachedFileWrapper: styled(motion.div)`
		display: flex;
		flex-direction: column;
		flex-shrink: 0;
		width: 80%;

		@container ChatBubbleContainer (min-width: 400px) {
			width: 60%;
		}
	`,
	redRobinMessageIcon: styled(Icon)`
		fill: ${tokens.color.content.muted};
	`,
};

const linkElement = (url: string, text: string): JSX.Element => (
	<elements.messageAnchorTag href={url} target='_blank' rel='noreferrer'>
		{text}
	</elements.messageAnchorTag>
);

function _ChatBubble(
	{
		message,
		isMessageStreak,
		isNewSender,
		isLastMessageInStreak,
		tabIndex,
		onRead,
		windowHasFocus,
		peerReadTimestamp,
		peerReadFullName,
		...rest
	}: Props,
	ref: Ref<HTMLDivElement>,
): JSX.Element {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const shouldObfuscateName = useContext(ObfuscateNameContext);
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const dateService = useService(DateService);
	const messageIsEmpty = message.text === null || message.text === '';
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const timestampRef = useRef(null);
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const [hasBeenRead, setHasBeenRead] = useState(message.hasBeenRead);

	const timeStamp = message.timestamp.format(
		dateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_IN_CURRENT_YEAR_NO_WEEKDAY),
	);

	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	useEffect(
		function toPreventInMemMessageAnimationToReplay() {
			message.hasRendered = true;
		},
		[message],
	);

	const displayName = shouldObfuscateName ? (message.fromInitials ?? '') : message.senderName;
	const displayNameFull = shouldObfuscateName ? (message.fromInitials ?? '') : message.senderNameWithTitle;

	const getFormsA11ySuffix = (): string => {
		if (message.forms.length > 1) {
			return '. ' + withTranslation('PW.Component.Chat.A11y.MessageHasForms');
		}
		if (message.forms.length > 0) {
			return '. ' + withTranslation('PW.Component.Chat.A11y.MessageHasForm');
		}
		return '';
	};

	const getAttachmentsA11ySuffix = (): string => {
		if (message.staleFileWorkers.length > 1) {
			return ', ' + withTranslation('PW.Component.Chat.A11y.MessageHasAttachments');
		}
		if (message.staleFileWorkers.length > 0) {
			return ', ' + withTranslation('PW.Component.Chat.A11y.MessageHasAttachment');
		}
		return '';
	};

	const ariaLabel =
		withTranslation(message.isMe ? 'PW.Component.Chat.A11y.ISaid' : 'PW.Component.Chat.A11y.OtherParticipantSays', {
			name: displayName,
		}) +
		(', ' + (message.text ?? '')) +
		'. ' +
		withTranslation('PW.Component.Chat.A11y.AtTime', { timeStamp }) +
		getFormsA11ySuffix() +
		getAttachmentsA11ySuffix();

	function handleVisible(): void {
		setHasBeenRead(true);
		onRead(message);
	}

	const getAvatar = (): JSX.Element => {
		if (message.isAutoreplyMessage) {
			return message.senderImageUrl ? (
				<Avatar src={message.senderImageUrl} name='' size='small' />
			) : (
				<Avatar icon='house' size='small' />
			);
		}

		return <Avatar src={message.senderImageUrl} name={message.fromInitials ?? displayName} size='small' />;
	};

	const renderSenderInfo = () => {
		if (!isNewSender) {
			return null;
		}
		if (message.isMe) {
			return <Spacer vertical={tokens.spacing.x2_5} />;
		}
		return <elements.senderInfoContainer aria-hidden='true'>{displayNameFull}</elements.senderInfoContainer>;
	};

	return (
		<elements.container tabIndex={tabIndex} role='listitem' aria-label={ariaLabel} ref={ref} {...rest}>
			{!message.isMe ? (
				<elements.imageContainer aria-hidden='true'>{isLastMessageInStreak ? getAvatar() : null}</elements.imageContainer>
			) : null}
			<elements.inner me={message.isMe}>
				{renderSenderInfo()}
				{message.isRedRobinInitialMessage ? (
					<Flex direction='row'>
						<elements.redRobinMessageIcon type='paperClip' size='small' />
						<Spacer horizontal={tokens.spacing.x0_5} />
						<Text
							variant='bodySmallRegular'
							style={{
								color: tokens.color.content.muted,
							}}
						>
							{withTranslation('PW.Component.Chat.Triage.Info.Message')}
						</Text>
					</Flex>
				) : null}
				{!messageIsEmpty ? (
					<elements.messageBubble
						className={classNames({
							other: !message.isMe,
							me: message.isMe,
							streak: isMessageStreak,
						})}
						initial={message.hasRendered ? false : { scale: 0 }}
						animate={{ scale: 1 }}
						transition={{
							type: 'spring',
							stiffness: 240,
							damping: 20,
						}}
					>
						<Text variant='bodyRegular'>
							<LinkInMessage text={message.text ?? ''} linkElement={linkElement} />
						</Text>

						<elements.timestamp ref={timestampRef} aria-hidden='true'>
							{timeStamp}
						</elements.timestamp>
					</elements.messageBubble>
				) : null}
				{message.forms.length > 0 ? (
					<Box height='unset' aria-label={withTranslation('PW.Component.Chat.A11y.ListOfForms')}>
						<Spacer vertical={tokens.spacing.x0_5} />

						<Flex grow={1} gap={tokens.spacing.x0_5}>
							{message.forms.map((formWorker) => (
								<FormCard
									key={formWorker.formId}
									disabled={tabIndex === -1}
									analyticAction='Viewed form in chat'
									formWorker={formWorker}
								/>
							))}
						</Flex>
					</Box>
				) : null}
				{message.staleFileWorkers.length > 0 ? (
					<Box
						height='unset'
						aria-label={withTranslation('PW.Component.Chat.A11y.ListOfAttachments')}
						align={message.isMe ? 'end' : 'start'}
						style={{ marginTop: tokens.spacing.x0_5 }}
						asChild
					>
						<Flex grow={1} gap={tokens.spacing.x0_5} align={message.isMe ? 'end' : 'start'}>
							{message.staleFileWorkers.map((staleFileWorker) => {
								return (
									<elements.attachedFileWrapper
										key={staleFileWorker.id}
										initial={message.hasRendered ? false : { scale: 0 }}
										animate={{ scale: 1 }}
										transition={{
											type: 'spring',
											stiffness: 240,
											damping: 20,
										}}
									>
										<AttachedFile worker={staleFileWorker} />
									</elements.attachedFileWrapper>
								);
							})}
						</Flex>
					</Box>
				) : null}
				{peerReadTimestamp && peerReadFullName != null && (
					<ReadReceiptClientComponent
						peerReadTimestamp={peerReadTimestamp}
						peerReadFullName={peerReadFullName}
						dateService={dateService}
					/>
				)}
			</elements.inner>
			{hasBeenRead ? null : <ReadUntilContainer onVisible={handleVisible} shouldBeObserved={windowHasFocus} />}
		</elements.container>
	);
}
export const ChatBubble = forwardRef(_ChatBubble);
